import type { EditionsGalleryItemsParams } from '#lib/websites/editions/api/getEditionsGalleryPage.js';
import { EDITIONS_GALLERY_LIMIT } from '#lib/websites/editions/constants/gallery.js';
import { EditionsGallerySort } from '#lib/websites/editions/constants/sorting.js';
import type { FilterSections } from '$global/features/filter-drawer/types';
import { getAppliedFilters } from '$global/features/filter-drawer/utils/getAppliedFilters/getAppliedFilters';
import { getAppliedSort } from '$global/features/filter-drawer/utils/getAppliedSort/getAppliedSort';

type Params = NonNullable<EditionsGalleryItemsParams>;
type Sort = Params['sort'];

const SORTS: Sort[] = [
	EditionsGallerySort.Newest,
	EditionsGallerySort.Oldest,
	EditionsGallerySort.ArtistAsc,
	EditionsGallerySort.ArtistDsc,
] as Sort[];

// The GraphQL-shaped attribute objects the shared filter drawer produces for
// this page's three sections (getEditionsGalleryFilterSections): two checkbox
// lists and three toggles. A toggle emits `_eq: <value>` once touched, so its
// OFF position is `_eq: false` rather than an absent term.
interface AppliedEditionsFilter {
	artists?: { Artists_id: { id: { _in: string[] } } };
	edition_type?: { _in: string[] };
	timed_release?: { _eq: boolean };
	application_only?: { _eq: boolean };
	charity?: { _eq: boolean };
}

export const getEditionsGalleryBaseParams = (): Params => ({
	scope: 'gallery',
	sort: EditionsGallerySort.Newest as Sort,
	limit: EDITIONS_GALLERY_LIMIT,
	offset: 0,
});

export const getEditionsGalleryRestParams = ({
	searchParams,
	filterSections,
}: {
	searchParams: URLSearchParams;
	filterSections: FilterSections;
}): Params => {
	const applied = getAppliedFilters({
		searchParams,
		filterSections,
	}) as AppliedEditionsFilter[];

	const sort = getAppliedSort({
		searchParams,
		defaultSort: [EditionsGallerySort.Newest],
	})[0] as Sort;

	const params: Params = {
		...getEditionsGalleryBaseParams(),
		sort: SORTS.includes(sort) ? sort : (EditionsGallerySort.Newest as Sort),
	};

	applied.forEach((entry) => {
		if (entry.artists) {
			params.artists = entry.artists.Artists_id.id._in;
		}
		if (entry.edition_type) {
			params.materials = entry.edition_type._in;
		}
		if (entry.timed_release) {
			params.timedRelease = String(entry.timed_release._eq) as 'true' | 'false';
		}
		if (entry.application_only) {
			params.applicationOnly = String(entry.application_only._eq) as
				'true' | 'false';
		}
		if (entry.charity) {
			params.charity = String(entry.charity._eq) as 'true' | 'false';
		}
	});

	return params;
};
